import React from "react";
import { Crown, Sparkles, Award } from "lucide-react";

// 🖼️ When the user uploads the sheikh photo, point this to /assets/<filename>.jpg.
//   Recommended: portrait orientation 1200×1500px (4:5), JPEG quality 80.
//   Suggested filename: /assets/sheikh_pillow.jpg
const SHEIKH_IMAGE = "/assets/sheikh_pillow.jpg";

const FACTS = [
  {
    icon: Crown,
    label: "Used in $10M+ royal bedrooms",
    sub: "From Emirati palaces to Saudi master suites, the same hand-graded goose down rests behind every headboard.",
  },
  {
    icon: Sparkles,
    label: "Top 3% of clusters in the world",
    sub: "Only the finest down clusters — hand-selected, graded and matched to royal-suite specification.",
  },
  {
    icon: Award,
    label: "Burj Al Arab · Emirates Palace · Jumeirah",
    sub: "Built to the spec of the world's only 7-star hotel and the most expensive bedrooms on earth.",
  },
];

export default function RoyalsApproved() {
  return (
    <section
      data-testid="royals-approved"
      className="relative bg-[#fafaf9] py-20 md:py-28 overflow-hidden"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* IMAGE SLOT — drop the sheikh / luxury suite portrait here */}
          <div className="lg:col-span-5">
            <div
              data-testid="sheikh-image-slot"
              className="relative aspect-[4/5] rounded-[28px] overflow-hidden shadow-2xl bg-[#0e0e10]"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(14,14,16,0.25), rgba(14,14,16,0.45)), url(${SHEIKH_IMAGE})`,
                backgroundSize: "cover",
                backgroundPosition: "65% center",
              }}
            >
              {/* Decorative inner glow */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e10]/75 via-transparent to-transparent" />

              {/* Sticker badge — corner */}
              <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-[#d8f06b] text-[#0e0e10] px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-extrabold">
                <Crown size={13} /> Royal grade
              </div>

              {/* Caption */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#d8f06b] font-extrabold">
                  As seen in
                </div>
                <div className="mt-1 text-white font-black text-[18px] md:text-[22px] leading-tight tracking-tight">
                  Dubai Royal Suites · Burj Al Arab
                </div>
                <div className="mt-1 text-white/70 text-[12px]">
                  Where billionaires fall asleep
                </div>
              </div>

              {/* Gold corner accent */}
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#b8952a]/30 blur-3xl pointer-events-none" />
            </div>
          </div>

          {/* TEXT */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#b8952a]">
              <span className="w-6 h-px bg-[#b8952a]/60" /> Royalty's secret
            </div>

            <h2 className="mt-4 text-[32px] sm:text-[44px] md:text-[54px] leading-[1.02] font-black tracking-[-0.025em] text-[#0e0e10]">
              The pillow billionaires
              <br />
              fall asleep on.
            </h2>

            <p className="mt-5 text-[15px] md:text-[17px] text-[#3a3a40] max-w-xl leading-relaxed">
              In the master suites of Dubai sheikhs, in the residences of Gulf royalty,
              in the most expensive bedrooms ever built — the pillow is always the same.
              <strong> Hand-graded goose down</strong> that costs more than most people's
              rent. We source the exact same down. We stitch it by the same hands.
              We just refused to charge what they charge. You shouldn't need a palace
              to feel like you slept in one.
            </p>

            <div className="mt-7 space-y-3 max-w-xl">
              {FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    className="flex items-start gap-3.5 rounded-2xl bg-white border border-black/5 p-4 hover:shadow-md transition"
                  >
                    <span className="shrink-0 w-10 h-10 rounded-xl bg-[#0e0e10] text-[#d8f06b] inline-flex items-center justify-center">
                      <Icon size={17} />
                    </span>
                    <div>
                      <div className="text-[13px] font-black text-[#0e0e10] leading-tight">
                        {f.label}
                      </div>
                      <div className="mt-1 text-[12px] text-[#5a5a60] leading-snug">
                        {f.sub}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <a
                href="#shop"
                data-testid="royals-cta"
                className="pill-btn inline-flex items-center gap-2 px-7 py-3.5 text-[13px]"
              >
                Bring it home →
              </a>
              <div className="text-[11px] text-[#8a8a8a]">
                Free worldwide shipping · 100 nights to decide
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
